import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import jsPDF from "jspdf";
import MainLayout from '../../layouts/MainLayout';

const subjectNames = [
  "Mathematics",
  "Science",
  "English",
  "History",
  "Art",
  "Music",
  "Physical Education",
  "Computer Science",
  "Business Studies",
  "Psychology",
  "Foreign Languages",
  "Environmental Science",
  "Philosophy",
];

const BloomRoom = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();

  const [userId, setUserId] = useState(authState?.user?._id || "");
  const [userName, setUserName] = useState(authState?.user?.name || "");
  const [userCode, setUserCode] = useState(authState?.user?.think5whyscode || "");
  
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("Mathematics");
  const [savedBlooms, setSavedBlooms] = useState([]);
  const [studentInfo, setStudentInfo] = useState({
    studentCode: "",
    session: "",
    grade: "",
    section: "",
    teacherCode: "",
    institutionCode: "",
    question: "",
  });

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");

  const handleInputChange = (e) => {
    setStudentInfo({ ...studentInfo, [e.target.name]: e.target.value });
  };

  const generateAIPrompt = () => {
    if (!studentInfo.question) {
      alert("Please enter a passage before generating questions.");
      return;
    }
    const prompt = `Evaluate the following passage and create 2 questions each based on Bloom's Taxonomy different levels:\n${studentInfo.question}`;
    setAiPrompt(prompt);
  };

  const saveBloom = () => {
    if (!aiPrompt) {
      alert("No AI-generated questions to save!");
      return;
    }
    setSavedBlooms([...savedBlooms, aiPrompt]);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
    const pageHeight = doc.internal.pageSize.getHeight(); // Get page height
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let y = margin;
  
    // Set title
    doc.setFont("helvetica", "bold");
    doc.text("Bloom Report", margin, y);
    doc.setFont("helvetica", "normal");
    y += 10; // Move down after title
  
    // Function to handle automatic page breaks
    const checkPageEnd = (increment = 10) => {
      if (y + increment > pageHeight - margin) {
        doc.addPage();
        y = margin; // Reset y for new page
      }
    };
  
    // Iterate over student info and wrap text if needed
    Object.entries(studentInfo).forEach(([key, value]) => {
      const text = `${key}: ${value}`;
      const splitText = doc.splitTextToSize(text, maxWidth);
  
      checkPageEnd(splitText.length * 7); // Check if a new page is needed
      doc.text(splitText, margin, y);
      y += splitText.length * 7; // Adjust spacing dynamically
  
      y += 5; // Add a blank line for readability
      checkPageEnd(); // Ensure space before next entry
    });
  
    // Add AI-generated questions
    doc.setFont("helvetica", "bold");
    checkPageEnd(15);
    doc.text("Questions:", margin, y);
    doc.setFont("helvetica", "normal");
    y += 10;
  
    const aiText = `${aiResponse}`; // Assuming aiResponse contains the generated questions
    const wrappedAiText = doc.splitTextToSize(aiText, maxWidth);
  
    wrappedAiText.forEach((line) => {
      checkPageEnd(7);
      doc.text(line, margin, y);
      y += 7; // Line spacing
    });
  
    doc.save(`Personalised_Questions_${studentInfo.studentCode.trim()}_` +
    `${studentInfo.session.trim()}_` +
    `${studentInfo.grade.trim()}_` +
    `${studentInfo.section.trim()}_` +
    `${subject.trim()}_` +
    `${studentInfo.institutionCode.trim()}_` +
    `${studentInfo.teacherCode.trim()}.pdf`);

  };
  

  return (
    <MainLayout>
      <div className="p-6 mx-auto bg-gray-800 shadow-lg rounded-lg text-white">
        <h2 className="text-2xl font-bold mb-4 flex justify-center text-yellow-200">Bloom Room</h2>

        <div className="mb-4 grid grid-cols-2 gap-4">
          {["studentCode", "session", "grade", "section", "teacherCode", "institutionCode"].map((field) => (
            <input
              key={field}
              name={field}
              placeholder={field.replace(/([A-Z])/g, " $1")}
              value={studentInfo[field]}
              onChange={handleInputChange}
              className="p-2 border rounded bg-gray-700 text-white w-full"
            />
          ))}
        </div>

        <div className="mb-4">
          <label className="font-semibold">Select Subject:</label>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="ml-2 p-2 border rounded bg-gray-700 text-white h-10"
          >
            {subjectNames.map((subj, index) => (
              <option key={index} value={subj}>
                {subj}
              </option>
            ))}
          </select>
        </div>

        <textarea
          name="question"
          value={studentInfo.question}
          onChange={handleInputChange}
          className="w-full p-2 mb-4 bg-gray-200 text-black"
          placeholder="Enter your paragraph here..."
          style={{ width: "100%", height: "250px" }}
        />

        <div className="flex flex-row justify-center mb-4">
          <button
            onClick={generateAIPrompt}
            className="bg-orange-500 p-2 m-1 rounded hover:bg-orange-600"
          >
            Generate Questions Based on Bloom's Taxonomy
          </button>
        </div>

        {aiPrompt && (
          <div className="px-2 py-2 border-2 border-blue-500 mb-4">
            <p className="mt-2 text-sm text-yellow-300">AI Prompt:</p>
            <p className="text-white">{aiPrompt}</p>
          </div>
        )}

        <textarea
          name="aiResponse"
          value={aiResponse}
          onChange={(e) => setAiResponse(e.target.value)}
          className="w-full p-2 mb-4 bg-gray-200 text-black"
          placeholder="Paste AI Response here..."
          style={{ width: "100%", height: "250px" }}
        />

        <div className='flex flex-row justify-center'>
          <button
            onClick={saveBloom}
            className="mr-8 mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Save Bloom
          </button>
          <button 
            onClick={generatePDF} 
            className="bg-red-500 p-2 mt-4 rounded hover:bg-red-600"
          >
            Download Bloom
          </button>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-bold">Saved Questions Based on Bloom's Taxonomy</h3>
          <ul className="mt-2">
            {savedBlooms.length === 0 ? (
              <p className="text-gray-400">No saved blooms yet.</p>
            ) : (
              savedBlooms.map((bloom, index) => (
                <li key={index} className="mt-2 p-2 bg-gray-700 rounded">
                  {bloom}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </MainLayout>
  );
};

export default BloomRoom;
